import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Archive, ChevronRight, FileClock, HardDrive, KeyRound, ScanSearch, Settings2, SlidersHorizontal, Users, Webhook } from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api, ep } from './lib/api.js';
import { ApiKeysTab } from './components/ApiKeysTab.jsx';
import { AuditTab } from './components/AuditTab.jsx';
import { IndexingTab } from './components/IndexingTab.jsx';
import { PoliciesTab } from './components/PoliciesTab.jsx';
import { RetentionTab } from './components/RetentionTab.jsx';
import { SettingsTab } from './components/SettingsTab.jsx';
import { StorageTab } from './components/StorageTab.jsx';
import { UsersTab } from './components/UsersTab.jsx';
import { WebhooksTab } from './components/WebhooksTab.jsx';

// Labels are i18n KEYS resolved at render — a module-level constant cannot
// hold translated text (it would freeze the language at import time).
const TABS = [
  { value: 'api-keys', labelKey: 'shell.tabs.apiKeys', icon: KeyRound, Component: ApiKeysTab },
  { value: 'webhooks', labelKey: 'shell.tabs.webhooks', icon: Webhook, Component: WebhooksTab },
  { value: 'indexing', labelKey: 'shell.tabs.indexing', icon: ScanSearch, Component: IndexingTab },
  { value: 'audit', labelKey: 'shell.tabs.audit', icon: FileClock, Component: AuditTab },
  { value: 'storage', labelKey: 'shell.tabs.storage', icon: HardDrive, Component: StorageTab },
  { value: 'users', labelKey: 'shell.tabs.users', icon: Users, Component: UsersTab },
  { value: 'policies', labelKey: 'shell.tabs.policies', icon: SlidersHorizontal, Component: PoliciesTab },
  { value: 'retention', labelKey: 'shell.tabs.retention', icon: Archive, Component: RetentionTab },
  { value: 'settings', labelKey: 'shell.tabs.settings', icon: Settings2, Component: SettingsTab },
];
// The signing section lives in Policies now — old links still land there.
const TAB_ALIASES = { signing: 'policies' };

export const Console = ({ projectId: projectIdProp = null, projectName = null, embedded = false }) => {
  const { t } = useTranslation('console');
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Embedded (office-portal iframe): identity arrives as props from the
  // console-token context — the root-plane projects lookup is unavailable
  // to embed users and must be skipped.
  const projectId = projectIdProp ?? params.id;
  const [project, setProject] = useState(projectName ? { name: projectName } : null);

  const requestedTab = TAB_ALIASES[searchParams.get('tab')] || searchParams.get('tab');
  const tab = TABS.some((t) => t.value === requestedTab) ? requestedTab : 'api-keys';
  // Tabs mount lazily — only what the admin opened fetches — and stay
  // mounted once visited, so switching back keeps state (page, filters, a
  // running rebuild's card) without a refetch.
  const [visited, setVisited] = useState(() => new Set([tab]));
  useEffect(() => {
    setVisited((prev) => (prev.has(tab) ? prev : new Set(prev).add(tab)));
  }, [tab]);

  useEffect(() => {
    if (embedded) return undefined;
    let cancelled = false;
    api.get(ep.projects()).then(({ success, response }) => {
      if (!cancelled && success && response?.projects) {
        setProject(response.projects.find((p) => String(p.id) === String(projectId)) || null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, embedded]);

  const setTab = (value) => {
    setSearchParams({ tab: value }, { replace: true });
  };

  return (
    <div className="w-full h-full flex flex-col">
      <Breadcrumb className="mb-6">
        <BreadcrumbList>
          {!embedded && (
            <>
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={() => navigate('/projects')}
                  className="cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                >
                  {t('shell.breadcrumb.projects')}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator>
                <ChevronRight className="h-4 w-4" />
              </BreadcrumbSeparator>
            </>
          )}
          <BreadcrumbItem>
            <BreadcrumbPage>
              {project?.name || t('shell.breadcrumb.projectFallback', { id: projectId })}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex items-start justify-between mb-6">
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-foreground mb-2">{t('shell.title')}</h1>
          <p className="text-muted-foreground text-base">{t('shell.tagline')}</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col">
        <div className="mb-4 -mx-1 overflow-x-auto px-1">
          <TabsList className="h-10 w-max">
            {TABS.map((tabItem) => (
              <TabsTrigger key={tabItem.value} value={tabItem.value} className="px-3 sm:px-4 gap-2" title={t(tabItem.labelKey)}>
                <tabItem.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{t(tabItem.labelKey)}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {TABS.map((tabItem) => (
          <TabsContent
            key={tabItem.value}
            value={tabItem.value}
            forceMount={visited.has(tabItem.value) ? true : undefined}
            className={tabItem.value === tab ? '' : 'hidden'}
          >
            {visited.has(tabItem.value) && <tabItem.Component projectId={projectId} projectName={project?.name} />}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};
